import { Typography } from '@navi/web-ui/lib/primitives';
import { debounce } from '@navi/web-ui/lib/utils';
import cx from 'classnames';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { GLOBAL } from 'src/constants/Global';
import usePolling from 'src/hooks/usePolling';
import {
  getTodayDate,
  getYesterdayDate
} from 'src/pages/CaseDetails/constants/communicationHistory.constant';
import { setCaseBrowsingHistoryDetails } from 'src/reducers/caseHistorySlice';
import { RootState } from 'src/store';
import { formatAmountRoundUp } from 'src/utils/commonUtils';
import { dateFormat, DateFormat, getFormatDate } from 'src/utils/DateHelper';
import Tooltip, { directions } from '../Tooltip';
import { today, yesterday } from './CaseBrowsingHistory.constant';
import styles from './CaseBrowsingHistory.module.scss';
import { fetchCaseHistoryPolling } from './CaseHistoryActions';
import { ICaseBrowsingHistory } from './interfaces';
import { getLink } from '../../utils/commonUtils';

const transformCaseHistory = (caseHistoryDetails: ICaseBrowsingHistory[]) => {
  const transformedCaseHistoryData: { [date: string]: ICaseBrowsingHistory[] } = {};
  for (const caseHistory of caseHistoryDetails) {
    const { lastViewedEpoc } = caseHistory;
    const date = getFormatDate(new Date(lastViewedEpoc), DateFormat.DD_MMM_YYYY);
    if (!transformedCaseHistoryData[date]) {
      transformedCaseHistoryData[date] = [];
    }
    transformedCaseHistoryData[date].push(caseHistory);
  }
  return transformedCaseHistoryData;
};

const getSectionLabel = (date: string) => {
  const todaysDate = getFormatDate(getTodayDate(), DateFormat.DD_MMM_YYYY);
  const yesterdaysDate = getFormatDate(getYesterdayDate(), DateFormat.DD_MMM_YYYY);
  return todaysDate == date ? today : yesterdaysDate == date ? yesterday : date;
};

const getHistoryDate = (lastViewedEpoc: number) => {
  return dateFormat(new Date(lastViewedEpoc), DateFormat.HH_mm_ampm);
};

const CaseBrowserHistory = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCaseHistory = async () => {
      const response = await fetchCaseHistoryPolling();
      dispatch(setCaseBrowsingHistoryDetails(response));
    };
    fetchCaseHistory();
  }, []);

  const caseBrowsingHistoryDetails = useSelector(
    (state: RootState) => state.caseHistoryDetail.caseBrowsingHistoryDetails
  );

  const caseSectionRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = () => {
    if (caseSectionRef.current) {
      caseSectionRef.current.scrollTop = 0;
    }
  };

  const titles = Array.from(document.getElementsByClassName('stickySection')) as Element[];

  const handleScroll = debounce(() => {
    titles?.forEach((title, index) => {
      const titlePosition = title.getBoundingClientRect().top;
      const nextTitle = titles[index + 1];
      const nextTitlePosition = nextTitle ? nextTitle.getBoundingClientRect().top : Infinity;
      if (titlePosition <= 85 && 85 < nextTitlePosition) {
        title.classList.add(styles.active);
      } else {
        title.classList.remove(styles.active);
      }
    });
  }, 100);

  const transformedCaseHistoryData = transformCaseHistory(caseBrowsingHistoryDetails);
  const renderCaseList = (transformedCaseHistoryData: {
    [date: string]: ICaseBrowsingHistory[];
  }) => {
    return Object.entries(transformedCaseHistoryData).map(([key, value], index) => {
      return (
        <div key={key} className={cx(styles.caseSectionContainer)} onClick={scrollToTop}>
          <Typography
            variant="p4"
            className={cx(styles.caseSectionHeading, 'stickySection', {
              [styles.active]: index == 0
            })}
          >
            {getSectionLabel(key)}
          </Typography>
          {value.map(details => {
            return (
              <Link
                key={details.lastViewedEpoc}
                className={styles.sectionVisitedCases}
                to={getLink({
                  accountNumber: details?.accountNumber,
                  customerReferenceId: details?.customerReferenceId,
                  caseReferenceId: details?.caseReferenceId,
                  caseBusinessVertical: details?.businessVertical
                })}
              >
                <Tooltip
                  direction={directions.TOP}
                  content={`${getHistoryDate(details.lastViewedEpoc)}`}
                >
                  <Typography variant="p3" className={styles.caseVisitedTime}>
                    {getHistoryDate(details.lastViewedEpoc)}
                  </Typography>
                </Tooltip>

                <Tooltip direction={directions.TOP} content={`${details.customerName}`}>
                  <Typography variant="p3" className={styles.caseVisitedCustomerName}>
                    {details.customerName}
                  </Typography>
                </Tooltip>

                <Tooltip
                  direction={directions.TOP}
                  content={`${formatAmountRoundUp(details.totalUnpaidDueAmount)}`}
                  wrapperClassName={styles.toolTipRight}
                  showToolTip={formatAmountRoundUp(details.totalUnpaidDueAmount)?.length > 8}
                >
                  <Typography variant="p3" className={styles.caseVisitedAmountDue}>
                    {formatAmountRoundUp(details.totalUnpaidDueAmount)}
                  </Typography>
                </Tooltip>
              </Link>
            );
          })}
        </div>
      );
    });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <>
      {caseBrowsingHistoryDetails?.length ? (
        <div
          className={styles.caseSectionContainerParent}
          ref={caseSectionRef}
          onScroll={handleScroll}
        >
          {renderCaseList(transformedCaseHistoryData)}
        </div>
      ) : (
        <div className={styles.dataNotAvailabel}>
          <div className={styles.text}>No browsing history to show</div>{' '}
        </div>
      )}
    </>
  );
};

export default CaseBrowserHistory;
